import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import formatApiError from '../../../utils/formatApiError';
import ServiceAccountAccessSteps from '../components/ServiceAccountAccessSteps';
import '../AIGarage.css';

// Mirrors the backend validator (SecScanRepoCreate.validate_git_url) so a typo
// is caught here instead of coming back as a 422.
const isSupportedGitUrl = (value) => /^https:\/\/\S+$/i.test(value.trim());

const AddSecScanRepoModal = ({ isOpen, onClose, onSubmit, serviceUser }) => {
  const [gitUrl, setGitUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [defaultRef, setDefaultRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setGitUrl('');
    setDisplayName('');
    setDefaultRef('');
    setError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!gitUrl.trim()) return;

    if (!isSupportedGitUrl(gitUrl)) {
      setError('Enter an https:// git URL, for example https://github.com/org/repo.git');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      await onSubmit({
        gitUrl: gitUrl.trim(),
        displayName: displayName.trim() || undefined,
        defaultRef: defaultRef.trim() || undefined,
      });
      onClose();
    } catch (err) {
      setError(formatApiError(err, 'Could not add repository'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="garage-modal-overlay" onClick={!submitting ? onClose : undefined}>
      <div className="garage-modal" onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <h2>Add repository</h2>
          <button className="garage-modal-close" onClick={onClose} disabled={submitting}>
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          <form onSubmit={handleSubmit}>
            <div className="garage-form-group">
              <label htmlFor="sec-scan-git-url">Git URL</label>
              <input
                id="sec-scan-git-url"
                type="url"
                inputMode="url"
                value={gitUrl}
                onChange={(e) => setGitUrl(e.target.value)}
                placeholder="https://github.com/org/repo.git"
                aria-describedby="sec-scan-git-url-hint"
                disabled={submitting}
                autoFocus
                required
              />
              <p id="sec-scan-git-url-hint" className="garage-field-hint">
                Must be an https:// URL. SSH URLs (git@…) are not supported.
              </p>
              <ServiceAccountAccessSteps serviceUser={serviceUser} />
            </div>

            <div className="garage-form-group">
              <label htmlFor="sec-scan-name">
                Name <span className="garage-optional">(Optional)</span>
              </label>
              <input
                id="sec-scan-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Defaults to the repository name"
                disabled={submitting}
              />
            </div>

            <div className="garage-form-group">
              <label htmlFor="sec-scan-ref">
                Branch <span className="garage-optional">(Optional)</span>
              </label>
              <input
                id="sec-scan-ref"
                value={defaultRef}
                onChange={(e) => setDefaultRef(e.target.value)}
                placeholder="Defaults to the default branch"
                disabled={submitting}
              />
            </div>

            {error && <div className="garage-error-message">{error}</div>}

            <p className="garage-field-hint">
              This only registers the repository. Open it afterward and pick a scan
              type's tab to run Security Scan or Code Review.
            </p>

            <div className="garage-modal-actions">
              <button
                type="button"
                className="garage-btn garage-btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button type="submit" className="garage-btn garage-btn-primary" disabled={submitting}>
                {submitting ? 'Adding…' : 'Add repository'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};

export default AddSecScanRepoModal;
